import {
  CompareFeature,
  CompareProduct,
  CompareTooltip,
} from '../../../../utils/types/types';

export const ENERGY_RATING_LEGACY_TITLE_SUFFIX = ' - A+++ to D';
export const ENERGY_RATING_TITLE_SUFFIX = ' - A to G';

type ProductFeatures = {
  productId: string;
  features: CompareFeature[];
};

type AddSuffixToEnergyRatingTitleProps = {
  title: string;
  hasLegacyAndNonLegacyEnergyRatings: boolean;
  isEnergyRatingLegacy: boolean;
};

type EnergyRatingTitleWithTooltipProps = {
  allProducts: CompareProduct[];
  hasLegacyAndNonLegacyEnergyRatings: boolean;
  isEnergyRatingLegacy: boolean;
};

export const addSuffixToEnergyRatingTitle = ({
  title,
  hasLegacyAndNonLegacyEnergyRatings,
  isEnergyRatingLegacy,
}: AddSuffixToEnergyRatingTitleProps): string => {
  if (!hasLegacyAndNonLegacyEnergyRatings) return title;

  return isEnergyRatingLegacy
    ? `${title}${ENERGY_RATING_LEGACY_TITLE_SUFFIX}`
    : `${title}${ENERGY_RATING_TITLE_SUFFIX}`;
};

export const energyRatingTitleWithTooltip = ({
  allProducts,
  hasLegacyAndNonLegacyEnergyRatings,
  isEnergyRatingLegacy,
}: EnergyRatingTitleWithTooltipProps): CompareTooltip[] => {
  const productWithEnergyRating = allProducts.find((product) =>
    isEnergyRatingLegacy
      ? product.energyRatingData?.rating &&
        product.energyRatingData.rating.legacy
      : product.energyRatingData?.rating &&
        !product.energyRatingData.rating.legacy
  );

  if (!productWithEnergyRating) return [];

  const energyRating = productWithEnergyRating.energyRatingData?.rating;
  const { title, description } = energyRating;

  return [
    {
      title,
      displayTitle: addSuffixToEnergyRatingTitle({
        title,
        hasLegacyAndNonLegacyEnergyRatings,
        isEnergyRatingLegacy,
      }),
      tooltip: description,
      isEnergyRatingValue: !isEnergyRatingLegacy,
      isEnergyRatingLegacyValue: isEnergyRatingLegacy,
    },
  ];
};

export const featureTitlesWithToolTips = (
  allProducts: CompareProduct[]
): CompareTooltip[] =>
  [
    allProducts.flatMap((product) => {
      const productEnergyRatingTitle = product.energyRatingData?.rating?.title
        ? product.energyRatingData.rating.title
        : '';
      return product.features
        .filter((feature) => feature.title !== productEnergyRatingTitle)
        .map((feature) => {
          return {
            title: feature.title,
            displayTitle: feature.title,
            tooltip: feature.tooltip,
            isEnergyRatingValue: false,
            isEnergyRatingLegacyValue: false,
          };
        });
    }),
  ].flat();

export const getFeaturesForAllProducts = (
  allProducts: CompareProduct[],
  hasLegacyAndNonLegacyEnergyRatings: boolean
): ProductFeatures[] =>
  allProducts.map((product: CompareProduct) => {
    const { productId, features, energyRatingData } = product;

    const isEnergyRatingLegacy = energyRatingData?.rating?.legacy
      ? energyRatingData.rating.legacy
      : false;

    const energyRatingTitle = energyRatingData?.rating?.title
      ? energyRatingData.rating.title
      : '';

    return {
      productId,
      features: features.map((feature) =>
        hasLegacyAndNonLegacyEnergyRatings &&
        feature.title === energyRatingTitle
          ? {
              ...feature,
              displayTitle: addSuffixToEnergyRatingTitle({
                title: feature.title,
                hasLegacyAndNonLegacyEnergyRatings,
                isEnergyRatingLegacy,
              }),
            }
          : { ...feature, displayTitle: feature.title }
      ),
    };
  });

export const hasAnyProductsWithEnergyRating = (
  allProducts: CompareProduct[]
): boolean =>
  allProducts.some(
    (product) =>
      product.energyRatingData?.rating &&
      !product.energyRatingData.rating.legacy
  );

export const hasAnyProductsWithEnergyRatingLegacy = (
  allProducts: CompareProduct[]
): boolean =>
  allProducts.some(
    (product) =>
      product.energyRatingData?.rating && product.energyRatingData.rating.legacy
  );

export const uniqueTitlesWithToolTips = (
  allProducts: CompareProduct[]
): CompareTooltip[] =>
  Array.from(
    new Map(
      featureTitlesWithToolTips(allProducts).map((feature) => [
        feature.title,
        feature,
      ])
    ).values()
  ).sort((feat1, feat2) => (feat1.title > feat2.title ? 1 : -1));
